/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppRouter } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import { DialogService, WaitingMode, WaitingService } from '@gh/core-ui';
import {
  beforeCurrentDate,
  currentDate,
  getMonthLogicalName,
  isDatesEquals,
  isDateWeekend,
  replaceUrlSegment,
} from '@gh/core-util';
import { skip$ } from '@gh/rx/operators';
import { OfficeInfo, SupplierUserContext } from '@gh/supplier-shared';
import { chunk, clone } from 'lodash';
import { Subscription } from 'rxjs';
import { CalendarModel, getCalendar, SupplierCalendarBusinessService } from '../../resources';
import { supplierOrderInfoDialogRoute } from '../supplier-order-info-dialog';

const EMPTY_OFFICE: OfficeInfo = <any>{};

@Component({
  selector: 'gh-supplier-calendar',
  templateUrl: 'supplier-calendar.component.html',
  styleUrls: ['./supplier-calendar.component.scss'],
})
export class SupplierCalendarComponent implements OnInit, OnDestroy {
  calendarItems: CalendarModel[] = [];
  currentDate: Date = currentDate();
  currentDates: Date[][] = [];
  offices: OfficeInfo[];
  currentOffice: OfficeInfo;
  private calendarSubscription$: Subscription;

  get currentMonthName(): string {
    const monthName = getMonthLogicalName(this.currentDate.getMonth());
    return this.translations.get(`lbl_${monthName}`);
  }

  get itemHeight(): number {
    const ordersLimit = 4;
    const ordersHeight = 11;
    const spaceHeight = 40;

    let maxOrderCount = 0;
    this.currentDates
      .forEach((row: Date[]) => {
        row.forEach((item: Date) => {
          const ordersCount = this.getOrdersByDate(item).length;
          maxOrderCount = ordersCount > maxOrderCount ? ordersCount : maxOrderCount;
        });
      });

    return maxOrderCount <= ordersLimit ? 0 : maxOrderCount * ordersHeight + spaceHeight;
  }

  constructor(
    private userContext: SupplierUserContext,
    private appRouter: AppRouter,
    private dialogs: DialogService,
    private translations: TranslationService,
    private waitings: WaitingService,
    private businessService: SupplierCalendarBusinessService,
    private route: ActivatedRoute,
  ) {
  }

  ngOnInit(): void {
    this.getOffices();
    this.currentMonth();
    this.subscribeToNavigation();
  }

  ngOnDestroy(): void {
    this.calendarSubscription$.unsubscribe();
  }

  isNow(date: Date): boolean {
    return isDatesEquals(currentDate(), date);
  }

  isWeekEnd(date: Date): boolean {
    return isDateWeekend(date);
  }

  isDateBefore(date: Date): boolean {
    return beforeCurrentDate(date);
  }

  getOrdersByDate(date: Date): CalendarModel[] {
    return this.calendarItems.filter((item: CalendarModel) => this.dateHasOrder(date, item));
  }

  onOpenOrderInfo(date: Date): void {
    const orders = this.getOrdersByDate(date);
    this.dialogs.openWithInput(supplierOrderInfoDialogRoute, {
      date,
      orders,
    });
  }

  currentMonth(): void {
    this.currentDate = currentDate();
    this.getCalendarItems();
  }

  nextMonth(next: boolean): void {
    this.currentDate = new Date(
      this.currentDate.getFullYear(),
      next ? this.currentDate.getMonth() + 1 : this.currentDate.getMonth() - 1,
      this.currentDate.getDate(),
    );

    this.getCalendarItems();
  }

  onCurrentOfficeChange(office: OfficeInfo): void {
    this.currentOffice = office;
    this.redirectToOffice();
  }

  private redirectToOffice(): void {
    this.appRouter
      .navigateByUrl(replaceUrlSegment(this.appRouter.url, 1, this.currentOffice.id || 'all'));
  }

  private dateHasOrder(item: Date, order: CalendarModel): boolean {
    return isDatesEquals(item, order.deliveryDate);
  }

  private getCalendar(): void {
    const datesPerRow = 7;
    this.currentDates = chunk(getCalendar(this.currentDate), datesPerRow);
  }

  private getCalendarItems(): void {
    this.getCalendar();
    this.calendarSubscription$ = this.businessService
      .getCalendarItems(this.currentDate.getFullYear(), this.currentDate.getMonth(), this.currentOffice.id).pipe(
        this.waitings.operator(WaitingMode.Background))
      .subscribe((items: CalendarModel[]) => this.calendarItems = items);
  }

  private getEmptyOffice(): OfficeInfo {
    return {
      ...EMPTY_OFFICE,
      name: this.translations.get('lbl_all'),
    };
  }

  private getOffices(): void {
    const offices = clone(this.userContext.offices);
    this.offices = offices.length ? offices : [EMPTY_OFFICE];

    if (this.offices.length > 1) {
      this.offices.unshift(this.getEmptyOffice());
      this.currentOffice = this.offices[0];
    } else {
      this.currentOffice = this.userContext.currentOffice;
    }
  }

  private subscribeToNavigation(): void {
    this.route
      .params.pipe(
      skip$(1))
      .subscribe((params: any) => {
        this.currentOffice = 'all' === params.officeId ? this.offices[0] : this.userContext.currentOffice;
        this.getCalendarItems();
      });
  }
}
